import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, ArrowUp } from "lucide-react";
import { ABtn, AScore, AResult, PadButton } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const W = 320, H = 240, G = 0.6, JUMP = -11, SPD = 3.2;
const PLATFORMS = [
  { x: 0, y: 216, w: 90, h: 24 },
  { x: 120, y: 176, w: 64, h: 12 },
  { x: 210, y: 140, w: 56, h: 12 },
  { x: 120, y: 216, w: 200, h: 24 },
  { x: 40, y: 120, w: 44, h: 12 },
];
const COINS0 = [{ x: 150, y: 150 }, { x: 232, y: 114 }, { x: 56, y: 96 }, { x: 270, y: 190 }, { x: 180, y: 190 }];
const FLAG = { x: 300, y: 176 };

export default function SuperJumper() {
  const canvasRef = useRef(null);
  const g = useRef({});
  const move = useRef(0);
  const jump = useRef(false);
  const scoreRef = useRef(0);
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState("idle");

  const start = () => { g.current = { x: 16, y: 190, vx: 0, vy: 0, onG: false, coins: COINS0.map((c) => ({ ...c, got: false })), face: 1 }; scoreRef.current = 0; setScore(0); setStatus("playing"); sfx.beep(); };
  const doJump = () => { const s = g.current; if (s && s.onG) { s.vy = JUMP; s.onG = false; sfx.eat(); } };

  useEffect(() => {
    const kd = (e) => { if (["ArrowLeft", "a"].includes(e.key)) { move.current = -1; e.preventDefault(); } else if (["ArrowRight", "d"].includes(e.key)) { move.current = 1; e.preventDefault(); } else if (["ArrowUp", "w", " "].includes(e.key)) { doJump(); e.preventDefault(); } };
    const ku = (e) => { if (["ArrowLeft", "a", "ArrowRight", "d"].includes(e.key)) move.current = 0; };
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  useEffect(() => {
    const ctx = canvasRef.current.getContext("2d");
    const draw = () => {
      ctx.fillStyle = "#5c94fc"; ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = "#fff"; ctx.fillRect(40, 30, 30, 10); ctx.fillRect(220, 50, 36, 12);
      PLATFORMS.forEach((p) => { ctx.fillStyle = p.y > 200 ? "#7c3f00" : "#2D6A4F"; ctx.fillRect(p.x, p.y, p.w, p.h); ctx.fillStyle = p.y > 200 ? "#a15a10" : "#3fa06f"; ctx.fillRect(p.x, p.y, p.w, 4); });
      const s = g.current;
      (s.coins || []).forEach((c) => { if (!c.got) { ctx.fillStyle = "#e8b04b"; ctx.beginPath(); ctx.arc(c.x, c.y, 5, 0, 7); ctx.fill(); } });
      ctx.fillStyle = "#7c3f00"; ctx.fillRect(FLAG.x, FLAG.y - 34, 3, 34); ctx.fillStyle = "#ef4444"; ctx.fillRect(FLAG.x + 3, FLAG.y - 34, 14, 10);
      ctx.fillStyle = "#c1121f"; ctx.fillRect((s.x || 0), (s.y || 0), 14, 18); ctx.fillStyle = "#fcd9b6"; ctx.fillRect((s.x || 0) + 2, (s.y || 0) - 5, 10, 6);
    };
    if (status !== "playing") { draw(); return; }
    let raf;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = g.current;
      if (jump.current) { doJump(); jump.current = false; }
      s.vx = move.current * SPD; if (move.current) s.face = move.current;
      s.x += s.vx; s.vy += G; s.y += s.vy;
      if (s.x < 0) s.x = 0; if (s.x > W - 14) s.x = W - 14;
      s.onG = false;
      for (const p of PLATFORMS) {
        if (s.x + 14 > p.x && s.x < p.x + p.w && s.y + 18 > p.y && s.y + 18 < p.y + p.h + 12 && s.vy >= 0) { s.y = p.y - 18; s.vy = 0; s.onG = true; }
      }
      for (const c of s.coins) { if (!c.got && Math.abs((s.x + 7) - c.x) < 12 && Math.abs((s.y + 9) - c.y) < 12) { c.got = true; scoreRef.current += 10; setScore(scoreRef.current); sfx.coin(); } }
      if (s.x + 14 > FLAG.x && s.y < FLAG.y && s.y > FLAG.y - 40) { cancelAnimationFrame(raf); sfx.win(); setStatus("win"); return; }
      if (s.y > H) { cancelAnimationFrame(raf); sfx.explode(); setStatus("over"); return; }
      draw();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [status]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-2"><AScore label="Coins" value={score / 10} /></div>
      <div className="relative">
        <canvas ref={canvasRef} width={W} height={H} className="w-full max-w-[320px] rounded-md border-[3px] border-kirana-brown" style={{ imageRendering: "pixelated", touchAction: "none" }} />
        {status !== "playing" && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/60 p-3">
            {status === "over" && <AResult win={false} text="You fell! 💥" testId="jumper-over" />}
            {status === "win" && <AResult win={true} text={`Reached the flag! 🚩`} testId="jumper-win" />}
            <ABtn tone="green" onClick={start} className="mt-3">{status === "idle" ? "Start" : "Play Again"}</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 text-center text-xs text-kirana-cream/70">Reach the 🚩 · collect coins</p>
      <div className="mt-1 flex items-center gap-3">
        <PadButton testId="jumper-left" onPress={() => { move.current = -1; }} onRelease={() => { move.current = 0; }}><ArrowLeft className="h-5 w-5" /></PadButton>
        <PadButton testId="jumper-jump" onPress={() => { jump.current = true; }} className="w-16 bg-kirana-red text-white"><ArrowUp className="h-5 w-5" /></PadButton>
        <PadButton testId="jumper-right" onPress={() => { move.current = 1; }} onRelease={() => { move.current = 0; }}><ArrowRight className="h-5 w-5" /></PadButton>
      </div>
    </div>
  );
}
